import {useTranslation} from '@/translations/i18n.tsx'
import {Controller, useForm} from 'react-hook-form'
import {Checkbox} from '@components/commons/checkbox'
import {useSubmitToAirbnb} from '@features/assignment/queries/useSubmitToAirbnb'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {getServiceType} from '@features/assignment/utils'
import {Assignment} from '@features/assignment/types.ts'
import {ModalBody, ModalFooter, ModalParagraph, ModalTitle} from '@components/ui/modal-atoms'
import {Button} from '@components/ui/button'
import {TextArea} from '@components/commons/textarea'
import {AlertCircleIcon} from '@components/ui/icon'
import {Dialog, DialogContent} from '@components/ui/dialog'
import {noUnsupportedChars} from '@utilities/helpers'

const notesSchema = noUnsupportedChars(z.string().nullable())

export const SubmitToAirbnbModal = ({
    onClose,
    assignment,
    setSuccessModalCb
}: {
    onClose: () => void
    assignment: Assignment
    setSuccessModalCb: () => void
}) => {
    const {t} = useTranslation()

    const requiredCheckbox = z.literal(true, {errorMap: () => ({message: t('errors:required')})})
    const photoValidationSchema = z.object({
        notes: notesSchema,
        confirm1: requiredCheckbox,
        confirm2: requiredCheckbox
    })
    const expVideoValidationSchema = z.object({
        notes: notesSchema,
        confirm1_exp: requiredCheckbox,
        confirm2_exp: requiredCheckbox,
        confirm3_exp: requiredCheckbox
    })
    const isExperienceVideo =
        getServiceType(assignment.task_media.name, assignment.task_category.name) == 'experience_video'

    const submitToAirbnbMutation = useSubmitToAirbnb({
        assignmentId: assignment.id,
        options: {
            onSuccess: () => {
                setSuccessModalCb()
                onClose()
            }
        }
    })

    const form = useForm({
        mode: 'onChange',
        resolver: zodResolver(isExperienceVideo ? expVideoValidationSchema : photoValidationSchema)
    })
    const errors = form.formState.errors

    const handleSubmit = form.handleSubmit(formValues => {
        submitToAirbnbMutation.mutate(formValues.notes)
    })

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="w-109.5" showCloseButton={false}>
                <ModalBody>
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-100 text-warning-600">
                        <AlertCircleIcon size={20} />
                    </div>
                    <div className="flex flex-col gap-2">
                        <ModalTitle>{t('assignment:descriptions:submit_to_airbnb_modal:title')}</ModalTitle>
                        <ModalParagraph>{t('assignment:descriptions:submit_to_airbnb_modal:subtitle')}</ModalParagraph>
                    </div>
                    {isExperienceVideo ? (
                        <div className="flex flex-col gap-2">
                            <Controller
                                name="confirm1_exp"
                                control={form.control}
                                render={({field}) => (
                                    <Checkbox
                                        id="confirm1_exp"
                                        onCheckedChange={checked => field.onChange(checked)}
                                        errorMessage={errors?.confirm1_exp?.message?.toString()}
                                        label={t(
                                            'assignment:descriptions:submit_to_airbnb_modal:checkbox_1_experience'
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="confirm2_exp"
                                control={form.control}
                                render={({field}) => (
                                    <Checkbox
                                        id="confirm2_exp"
                                        onCheckedChange={checked => field.onChange(checked)}
                                        errorMessage={errors?.confirm2_exp?.message?.toString()}
                                        label={t(
                                            'assignment:descriptions:submit_to_airbnb_modal:checkbox_2_experience',
                                            {
                                                listingId: assignment.entity.external_id,
                                                hostName: assignment.host.first_name
                                            }
                                        )}
                                    />
                                )}
                            />

                            <Controller
                                name="confirm3_exp"
                                control={form.control}
                                render={({field}) => (
                                    <Checkbox
                                        id="confirm3_exp"
                                        onCheckedChange={checked => field.onChange(checked)}
                                        errorMessage={errors?.confirm3_exp?.message?.toString()}
                                        label={t('assignment:descriptions:submit_to_airbnb_modal:checkbox_2')}
                                    />
                                )}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <Controller
                                name="confirm1"
                                control={form.control}
                                render={({field}) => (
                                    <Checkbox
                                        id="confirm1"
                                        onCheckedChange={checked => field.onChange(checked)}
                                        errorMessage={errors?.confirm1?.message?.toString()}
                                        label={t('assignment:descriptions:submit_to_airbnb_modal:checkbox_1', {
                                            listingId: assignment.entity.external_id,
                                            hostName: assignment.host.first_name
                                        })}
                                    />
                                )}
                            />

                            <Controller
                                name="confirm2"
                                control={form.control}
                                render={({field}) => (
                                    <Checkbox
                                        id="confirm2"
                                        onCheckedChange={checked => field.onChange(checked)}
                                        errorMessage={errors?.confirm2?.message?.toString()}
                                        label={t('assignment:descriptions:submit_to_airbnb_modal:checkbox_2')}
                                    />
                                )}
                            />
                        </div>
                    )}
                    <TextArea
                        placeholder={t('assignment:descriptions:submit_to_airbnb_modal:notes_placeholder')}
                        errorMessage={errors?.notes?.message?.toString()}
                        label={t('assignment:notes')}
                        {...form.register('notes')}
                    />
                </ModalBody>
                <ModalFooter>
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={submitToAirbnbMutation.isPending || !form.formState.isValid}
                    >
                        {t('commons:submit')}
                    </Button>
                </ModalFooter>
            </DialogContent>
        </Dialog>
    )
}
